import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const BACKEND = BACKEND_URL;

const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("ccdb_token");
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor — normalises 5xx / Cloudflare failures so the UI
// never shows raw HTML like "The origin web server sent a response that
// Cloudflare could not parse". Also transparently retries once on transient
// gateway errors (520/502/504/522/524) after a short delay because those
// almost always resolve on the next attempt (cold-start, brief overload).
const TRANSIENT_STATUSES = new Set([502, 503, 504, 520, 521, 522, 523, 524]);
const FRIENDLY_MESSAGE =
  "Server is warming up. Please try again in a moment.";
const CF_MARKERS = /origin web server|could not parse|cloudflare|<html|<!doctype|malformed http/i;

function _normaliseError(error) {
  const status = error?.response?.status;
  const isTransient = status && TRANSIENT_STATUSES.has(status);
  const is5xx = status && status >= 500;

  if (error?.response) {
    const raw = error.response.data;
    const rawStr = typeof raw === "string" ? raw : "";
    // Also check inside JSON `detail` / `message` — some platform ingresses
    // (Cloudflare, Emergent's proxy) wrap the origin's malformed response
    // into a JSON envelope like {"detail": "The origin web server sent…"},
    // which would otherwise slip past the raw-body regex.
    const jsonDetail = typeof raw === "object" && raw
      ? String(raw.detail || raw.message || "")
      : "";
    const looksLikeCloudflare =
      (rawStr && CF_MARKERS.test(rawStr)) ||
      (jsonDetail && CF_MARKERS.test(jsonDetail));
    const missingDetail =
      raw == null || (typeof raw === "object" && !raw.detail && !raw.message);

    // Force-replace when:
    //  - body OR detail-text matches a Cloudflare/HTML marker (any status)
    //  - status is transient (520/502/504/etc.) and body doesn't have a detail
    //  - status is any 5xx with a non-JSON body (string) — belt & braces
    if (looksLikeCloudflare || (isTransient && missingDetail) || (is5xx && rawStr)) {
      error.response.data = { detail: FRIENDLY_MESSAGE, _transient: isTransient, _status: status };
    }
  } else if (error && !error.response) {
    error.response = {
      status: 0,
      data: { detail: "Network unreachable. Check your connection and retry.", _transient: true },
    };
  }
  return error;
}

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const cfg = error?.config || {};
    const status = error?.response?.status;
    const isTransient = status && TRANSIENT_STATUSES.has(status);
    const method = (cfg.method || "get").toLowerCase();
    const url = String(cfg.url || "");

    // "Silent" mode — for background polls (badge counters, heartbeat
    // pings, dashboard auto-refresh). Callers opt in explicitly via
    // `api.get(url, { silent: true })` OR by matching a well-known
    // polling URL. When the request fails with a transient upstream
    // error AFTER retries, we resolve with an empty payload so the
    // caller's `.then(res => setState(res.data))` still runs cleanly
    // with `[]` or `{}` — no toast, no error banner, no user panic.
    //
    // Real user-triggered actions (login, approve, submit form) still
    // surface the friendly "warming up" message so the user knows to
    // retry manually. Page-load fetches are NOT silent by default —
    // if a page fails to load, we want the user to see the error.
    const KNOWN_POLLING_URLS = [
      "/dashboard/upcoming-events",
      "/dashboard/state-weather",
      "/dashboard/daily-quote",
    ];
    const isSilent =
      cfg.silent === true ||
      (method === "get" && KNOWN_POLLING_URLS.some((p) => url.includes(p)));

    // Aggressive retry policy for transient Cloudflare/origin blips —
    // Emergent's platform occasionally returns 520/502/504 during pod
    // restarts. We hide these from users by retrying up to twice with
    // exponential backoff for GETs, and once for known-idempotent POSTs.
    cfg.__retries = cfg.__retries || 0;
    const isSafeIdempotentPost =
      method === "post" &&
      (url.endsWith("/auth/login") ||
        url.endsWith("/contractor-auth/login") ||
        url.endsWith("/health"));
    const canRetry =
      isTransient &&
      cfg.__retries < 2 &&
      (method === "get" || isSafeIdempotentPost);

    if (canRetry) {
      cfg.__retries += 1;
      const delay = cfg.__retries === 1 ? 500 : 1500;
      await new Promise((res) => setTimeout(res, delay));
      try {
        return await api.request(cfg);
      } catch (_) { /* fall through to next retry OR normalise */ }
    }

    // After retries exhausted: for silent polls, resolve with a sensibly
    // empty response so callers don't blow up + no toast is shown.
    if (isSilent && isTransient) {
      const isListEndpoint =
        url.includes("/pending") ||
        url.includes("/events") ||
        url.includes("/ready-signals") ||
        url.includes("/roster");
      return Promise.resolve({
        status: 204,
        data: isListEndpoint ? [] : {},
        headers: {},
        config: cfg,
        _silentlyFailed: true,
      });
    }

    return Promise.reject(_normaliseError(error));
  },
);

// Attach the same normaliser to the DEFAULT axios instance so bare
// `axios.get(...)` calls scattered across drawers/portals also benefit
// without needing to migrate every callsite.
axios.interceptors.response.use(
  (r) => r,
  async (error) => Promise.reject(_normaliseError(error)),
);

export function fileUrl(path) {
  if (!path) return "";
  return `${API}/files/${path}`;
}

export default api;
